import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, Provider } from '@angular/core';
import {
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { TranslocoModule } from '@ngneat/transloco';
import { UntilDestroy } from '@ngneat/until-destroy';
import { SharedModule } from 'primeng/api';
import { MessagesModule } from 'primeng/messages';

import { ExecIVR, ExecIVRSchema } from '@pwp-common';

import { FormGroupControlValueAccessor } from '../../../generic/abstract-classes/form-group-control-value-accessor';
import { IvrAutocompleteComponent } from '../../ivrs/select/ivr-autocomplete/ivr-autocomplete.component';
import { ServiceCommandEditorOutput } from '../editor-output/service-command-editor-output';

const VALUE_ACCESSOR: Provider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => ServiceCommandEditorExecIvrComponent),
  multi: true,
};

const VALIDATOR: Provider = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => ServiceCommandEditorExecIvrComponent),
  multi: true,
};

@UntilDestroy()
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-service-command-editor-exec-ivr',
  standalone: true,
  templateUrl: './service-command-editor-exec-ivr.component.html',
  styleUrls: ['./service-command-editor-exec-ivr.component.scss'],
  imports: [CommonModule, IvrAutocompleteComponent, MessagesModule, ReactiveFormsModule, SharedModule, TranslocoModule],
  providers: [VALUE_ACCESSOR, VALIDATOR],
})
export class ServiceCommandEditorExecIvrComponent extends FormGroupControlValueAccessor<
  ServiceCommandEditorOutput,
  ServiceCommandEditorOutput
> {
  /////////////////////////////////////////////////////////////////////////////////////////////
  // Lifecycle
  /////////////////////////////////////////////////////////////////////////////////////////////

  constructor(public changeDetectorRef: ChangeDetectorRef) {
    super(changeDetectorRef);
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Define Form
  /////////////////////////////////////////////////////////////////////////////////////////////

  public defineForm() {
    const formConfig: Record<string, UntypedFormControl> = {};
    formConfig[ExecIVRSchema.commandName] = new UntypedFormControl(ExecIVRSchema.Defaults.commandName, [
      Validators.required,
    ]);
    formConfig[ExecIVRSchema.ivrId] = new UntypedFormControl(undefined, [Validators.required]);
    this.form = new UntypedFormGroup(formConfig);
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Parse Value Change
  /////////////////////////////////////////////////////////////////////////////////////////////

  // eslint-disable-next-line class-methods-use-this
  public parseValueChange(value: ServiceCommandEditorOutput): ServiceCommandEditorOutput {
    return ExecIVR.deserialize(value).serialize();
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Controls
  /////////////////////////////////////////////////////////////////////////////////////////////

  public get ivrId() {
    return this.form.get(ExecIVRSchema.ivrId) as UntypedFormControl;
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Validation Errors
  /////////////////////////////////////////////////////////////////////////////////////////////

  protected makeValidationErrors(): ValidationErrors {
    return {
      'service-command-editor-exec-ivr': this.form.value,
    };
  }
}
